import { useEffect, useState } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  type UnitSystem,
  getUnitPreference,
  setUnitPreference,
} from "@/hooks/useVolumeCalculator";

export interface UnitToggleProps {
  /** 当前选中的单位系统 */
  value?: UnitSystem;
  /** 单位切换回调 */
  onChange?: (unit: UnitSystem) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 公制标签 */
  metricLabel?: string;
  /** 英制标签 */
  imperialLabel?: string;
}

/**
 * 单位切换组件
 *
 * 支持公制（cm/L）和英制（in/gal）切换，
 * 使用 localStorage 持久化用户偏好。
 *
 * @example
 * ```tsx
 * const [unit, setUnit] = useState<UnitSystem>('metric');
 *
 * <UnitToggle value={unit} onChange={setUnit} />
 * ```
 */
export function UnitToggle({
  value,
  onChange,
  disabled = false,
  className,
  metricLabel = "公制",
  imperialLabel = "英制",
}: UnitToggleProps) {
  // 内部状态（当未提供 value 时使用）
  const [internalValue, setInternalValue] = useState<UnitSystem>(() =>
    getUnitPreference(),
  );

  // 使用受控或非受控模式
  const currentValue = value ?? internalValue;

  // 初始化时从 localStorage 读取偏好
  useEffect(() => {
    if (value === undefined) {
      const saved = getUnitPreference();
      setInternalValue(saved);
    }
  }, [value]);

  const handleChange = (newValue: string) => {
    // ToggleGroup 在取消选择时会传空字符串，忽略这种情况
    if (newValue !== "metric" && newValue !== "imperial") {
      return;
    }

    const unit = newValue as UnitSystem;

    // 持久化到 localStorage
    setUnitPreference(unit);

    // 更新内部状态
    setInternalValue(unit);

    // 触发回调
    onChange?.(unit);
  };

  return (
    <ToggleGroup
      type="single"
      value={currentValue}
      onValueChange={handleChange}
      disabled={disabled}
      className={className}
      variant="outline"
      size="sm"
    >
      <ToggleGroupItem value="metric" aria-label={metricLabel} className="px-4">
        {metricLabel}
      </ToggleGroupItem>
      <ToggleGroupItem
        value="imperial"
        aria-label={imperialLabel}
        className="px-4"
      >
        {imperialLabel}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export default UnitToggle;
